import Component, { mixins } from 'vue-class-component'
import collect, { Collection } from 'collect.js'
import { StockMolecule, StockEnzyme } from '@/interfaces/stock'
import ModelMolecule from '@/models/molecule'
import { moleculeCategories } from '@/constants/molecule'
import ModelEnzyme from '@/models/enzyme'
import ModelConnection from '@/models/connection'
import { IO } from '@/interfaces/connection'
import { ioTypes } from '@constants/io'
import MixinFats from './fats'
import { capitalizeFirstLetter } from '@shared/utils'
import { Pawn, Player } from '@/services/store/types'

@Component
export default class MixinStock extends mixins(MixinFats) {
  public get stockMolecules(): Collection<StockMolecule> {
    const stockMolecules: StockMolecule[] = this.activePlayer.molecules
      .map((playerMolecule: StockMolecule) => {
        const modelMolecule: ModelMolecule = this.$molecules.find(playerMolecule.ids[0])

        return {
          ...playerMolecule, ...{
            title: modelMolecule.title,
            type: modelMolecule.type,
            category: modelMolecule.category,
          },
        } as StockMolecule
      })

    return collect(stockMolecules)
  }

  public get stockEnzymes(): Collection<StockEnzyme> {
    const stockEnzymes = this.activeTeam.enzymes
      .map((teamEnzyme: StockEnzyme) => {
        const modelEnzyme: ModelEnzyme = this.$enzymes.find(teamEnzyme.id)

        return {
          ...teamEnzyme, ...{
            title: modelEnzyme.title,
            type: modelEnzyme.type,
          },
        } as StockEnzyme
      })

    return collect(stockEnzymes)
  }

  public get stockAcids(): Collection<StockMolecule> {
    return this.stockMolecules
      .filter((stockMolecule: StockMolecule) => stockMolecule.category === moleculeCategories.ACID)
  }

  public get stockFats(): Collection<StockMolecule> {
    return this.stockMolecules
      .filter((stockMolecule: StockMolecule) => stockMolecule.category === moleculeCategories.FAT)
      .transform((stockMolecule: StockMolecule) => {
        switch (stockMolecule.chainLengths.length) {
          case 1:
            if (stockMolecule.chainLengths[0] === 0) {
              stockMolecule.title = 'acetyl-coA'
            } else {
              stockMolecule.title = this.fatGetTitle(stockMolecule.chainLengths[0], stockMolecule.doubleBindings[0])
            }
            break
          case 3:
            stockMolecule.title = 'triacylglycerol'
            break
        }

        return stockMolecule
      })
  }

  public get stockSugars(): Collection<StockMolecule> {
    return this.stockMolecules
      .filter((stockMolecule: StockMolecule) => stockMolecule.category === moleculeCategories.SUGAR)
  }

  public stockGetMolecule(modelMolecule: ModelMolecule): StockMolecule {
    return this.stockMolecules
      .first((molecule: StockMolecule) => molecule.ids.includes(modelMolecule.id))
  }

  public stockGetEnzyme(modelEnzyme: ModelEnzyme): StockEnzyme {
    return this.stockEnzymes
      .first((enzyme: StockEnzyme) => enzyme.id === modelEnzyme.id)
  }

  public stockHasMolecule(modelMolecule: ModelMolecule): boolean {
    return this.stockGetMolecule(modelMolecule) !== undefined
  }

  public stockHasEnzyme(modelEnzyme: ModelEnzyme): boolean {
    return this.stockGetEnzyme(modelEnzyme) !== undefined
  }

  public stockRemoveMolecule(modelMolecule: ModelMolecule, chainLengths: number[] = [], doubleBindings: number[] = []) {
    this.storeRemovePlayerMolecule({
      player: this.activePlayer,
      modelMolecule,
      chainLengths,
      doubleBindings,
    })
  }

  public stockHasEnzymesForConnection(connection: ModelConnection) {
    let enzymesPresent: boolean = true

    connection.inputs
      .filter((input: IO) => input.objectType === ioTypes.ENZYME)
      .each((input: IO) => {
        const modelEnzyme: ModelEnzyme = this.$enzymes.find(input.objectID)
        const stockEnzyme: StockEnzyme = this.stockGetEnzyme(modelEnzyme)

        if (stockEnzyme === undefined || stockEnzyme.quantity < input.quantity) {
          enzymesPresent = false
          this.$flits.error(`Je hebt ${input.quantity}x ${modelEnzyme.title} nodig voor deze overgang.`)
        }
      })

    return enzymesPresent
  }

  public stockProcessConnectionUsage(connection: ModelConnection) {
    connection.inputs
      .filter((input: IO) => input.objectType === ioTypes.ENZYME)
      .each((input: IO) => {
        this.storeUpdateTeamEnzyme({
          team: this.activeTeam,
          enzyme: this.$enzymes.find(input.objectID),
          quantity: -1 * input.quantity,
        })
      })

    connection.outputs
      .filter((input: IO) => input.objectType === ioTypes.ENZYME)
      .each((input: IO) => {
        this.storeUpdateTeamEnzyme({
          team: this.activeTeam,
          enzyme: this.$enzymes.find(input.objectID),
          quantity: input.quantity,
        })
      })
  }

  public stockCreateProduct(modelMolecule: ModelMolecule, pawnPrevMolecule: Pawn): void {
    const deletePawn: Pawn = this.getPawnByPrevMoleculePawn(modelMolecule, pawnPrevMolecule)

    this.storeAddPlayerMolecule({
      player: this.activePlayer,
      ids: [modelMolecule.id, ...modelMolecule.mirrors],
      chainLengths: deletePawn.chainLengths,
      doubleBindings: deletePawn.doubleBindings,
    })

    this.storeRemovePawnByID(deletePawn.id)
    this.$flits.success(`${capitalizeFirstLetter(modelMolecule.title)} is toegevoegd aan je moleculen.`)
  }

  protected getPawnByPrevMoleculePawn(molecule: ModelMolecule, pawnPrevMolecule: Pawn) {
    let index = this.pawns.findIndex((pawn: Pawn) => {

      return pawn.playerID === this.activePlayer.id &&
        pawn.moleculeID === molecule.id &&
        pawn.chainLengths.join('-') === pawnPrevMolecule.chainLengths.join('-') &&
        pawn.doubleBindings.join('-') === pawnPrevMolecule.doubleBindings.join('-')
    })

    if (index === -1) {
      index = this.pawns.findIndex((pawn: Pawn) => {
        return pawn.playerID === null &&
          pawn.moleculeID === molecule.id &&
          pawn.chainLengths.join('-') === pawnPrevMolecule.chainLengths.join('-') &&
          pawn.doubleBindings.join('-') === pawnPrevMolecule.doubleBindings.join('-')
      })
    }

    if (index === -1) {
      const teamPlayer = this.players.find((item: Player) => {
        return item.teamID === this.activePlayer.teamID && item.id !== this.activePlayer.id
      }) as Player

      index = this.pawns.findIndex((pawn: Pawn) => {
        return pawn.playerID === teamPlayer.id &&
          pawn.moleculeID === molecule.id &&
          pawn.chainLengths.join('-') === pawnPrevMolecule.chainLengths.join('-') &&
          pawn.doubleBindings.join('-') === pawnPrevMolecule.doubleBindings.join('-')
      })
    }

    return this.pawns[index]
  }
}
